import { sortBy } from 'ramda'

import { Account } from '@ecm/data/Account'
import { Address } from '@ecm/data/Address'
import { Billing } from '@ecm/data/Billing'
import { Cart } from '@ecm/data/Cart'
import { CartInteraction } from '@ecm/data/Ecommerce'
import { OrderEstimate } from '@ecm/data/Order'
import { Plan } from '@ecm/data/Plan'
import { runClientEff } from '@ecm/effect'
import {
  buildAccountInformation,
  buildAddresses,
  buildBilling,
  buildPricingPlans,
  buildPurchase,
  buildReviewOrder,
} from '@ecm/effect/client/Ecommerce/BuildMethodInvoker'
import {
  mapCustomerDo,
  mapOrderDo,
  mapProductDo,
} from '@ecm/effect/client/Ecommerce/EcommerceMapper'

export const trackAccountInformation = (cart: Cart) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const products = cart.addresses.map((address, key) =>
        mapProductDo(cart.plan, address, key > 0)
      )

      eff.trackEcommerce(buildAccountInformation(products))
    }
  })
}

export const trackAddresses = (
  addresses: Address[],
  plan: Plan,
  cartAddresses: Address[],
  cartInteraction?: CartInteraction
) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const defaultProductPrice =
        cartInteraction &&
        cartAddresses.length + (cartInteraction === CartInteraction.AddToCart ? 1 : 0) > 1

      const products = addresses.map(address =>
        mapProductDo(plan, address, !cartInteraction || defaultProductPrice)
      )

      eff.trackEcommerce(buildAddresses(products, cartInteraction))
    }
  })
}

export const trackBilling = (
  account: Account,
  cart: Cart,
  orderEstimate: OrderEstimate,
  billing?: Billing
) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const customer = mapCustomerDo(account)
      const order = mapOrderDo(orderEstimate, billing, cart.coupon)

      const products = cart.addresses.map((address, key) =>
        mapProductDo(cart.plan, address, key > 0)
      )

      eff.trackEcommerce(buildBilling(products, customer, order))
    }
  })
}

export const trackPricingPlans = (
  plans: Plan[],
  address?: Address,
  defaultAddress?: Address,
  cartInteraction?: CartInteraction
) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const sortedPlans = sortBy((p: Plan) => p.price, plans)

      const products = sortedPlans.map(plan =>
        mapProductDo(plan, plan.restrictedAddress && plans.length > 1 ? defaultAddress : address)
      )

      eff.trackEcommerce(buildPricingPlans(products, cartInteraction))
    }
  })
}

export const trackPurchase = (
  account: Account,
  cart: Cart,
  orderEstimate: OrderEstimate,
  billing: Billing
) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const customer = mapCustomerDo(account, billing)
      const order = mapOrderDo(orderEstimate, billing, cart.coupon)

      const products = cart.addresses.map((address, key) =>
        mapProductDo(cart.plan, address, key > 0)
      )

      if (cart.addons.length > 0) {
        products.push(mapProductDo(cart.plan, undefined, false, cart.addons[0]))
      }

      eff.trackEcommerce(buildPurchase(products, customer, order))
    }
  })
}

export const trackReviewOrder = (
  account: Account,
  cart: Cart,
  orderEstimate: OrderEstimate,
  billing: Billing
) => {
  runClientEff(async eff => {
    if (eff.ask().featureToggle.ecommerceTracking) {
      const customer = mapCustomerDo(account, billing)
      const order = mapOrderDo(orderEstimate, billing, cart.coupon)

      const products = cart.addresses.map((address, key) =>
        mapProductDo(cart.plan, address, key > 0)
      )

      if (cart.addons.length > 0) {
        products.push(mapProductDo(cart.plan, undefined, false, cart.addons[0]))
      }

      eff.trackEcommerce(buildReviewOrder(products, customer, order))
    }
  })
}
